import { useState, useCallback, useEffect } from 'react';
import { Form } from 'antd';
import { useApplicationId } from '../../../hooks/useApplicationId';
import {
	Application,
	ApproverFeedback,
	Feedback,
	UserRole,
} from '../../../shared/VolunteerApplicationServiceTypes';
import { useCallApi } from '../../../hooks/useCallApi';
import { submitApproverRecommendation } from '../../../service/VolunteerApplicationService';
import { useAuth } from '../../../hooks/useAuth';
import { FUNCTIONARY_HOME, INTERVIEW_LANDING } from '../../../shared/Routes';
import { ReapplyCondition, ReviewAction } from '../../../shared/Constants';
import { useConfirmPopup } from '../../../hooks/useConfirmPopup';
import { useInfoPopup } from '../../../hooks/useInfoPopUp';
import ConfirmTick from '../../../assets/images/confirm-tick.svg';
import useUserSession from '../../../hooks/useUserSession';
import { resetApplicationsListCache } from '../ApplicationsList/useApplicationsList';

export const useApproverRecommendationPage = () => {
	const [recommendation, setRecommendation] = useState<ApproverFeedback>({});
	const [submitButtonDisabled, setSubmitButtonDisabled] =
		useState<boolean>(false);
	const [form] = Form.useForm<ApproverFeedback>();
	const values = Form.useWatch<ApproverFeedback>([], form);
	const { fetchApplication, applicationId, navigateWithApplicationId } =
		useApplicationId();
	const [application, setApplication] = useState<Application | undefined>();
	const [isApprover, setIsApprover] = useState<boolean | undefined>(false);
	const { callApi } = useCallApi();
	const { srcmId } = useAuth();
	const { selectedRole } = useUserSession();
	const { confirmPopUpAction } = useConfirmPopup();
	const { infoPopUpAction } = useInfoPopup();

	const onConfirmSubmit = useCallback(async () => {
		try {
			const reapplyCondition =
				recommendation.review === ReviewAction.APPROVE
					? ReapplyCondition.NOT_APPLICABLE_CONDITION
					: recommendation.reapplyCondition;

			await callApi(
				() =>
					submitApproverRecommendation({
						applicationId,
						functionarySrcmId: srcmId as string,
						recommendation: {
							review: recommendation.review,
							predefinedReasons: recommendation.predefinedReasons,
							comments: recommendation.comments,
							approverRevertedForwardedToCareTeamComments:
								recommendation.approverRevertedForwardedToCareTeamComments,
						},
						reapplyCondition,
						shouldCareTeamReview: recommendation.shouldCareTeamReview,
						approverRevertedForwardedToCareTeam:
							recommendation.approverRevertedForwardedToCareTeamComments &&
							recommendation.approverRevertedForwardedToCareTeamComments !== ''
								? true
								: false,
						selectedRoleCode: selectedRole as UserRole,
					}),
				'errorOccurredWhileSubmitting'
			);
			resetApplicationsListCache();
			infoPopUpAction(
				'submittedSuccessfully',
				'',
				() => {
					navigateWithApplicationId(FUNCTIONARY_HOME);
				},
				ConfirmTick
			);
		} catch (r) {
			// Do Nothing
		}
	}, [
		recommendation.review,
		recommendation.reapplyCondition,
		recommendation.predefinedReasons,
		recommendation.comments,
		recommendation.approverRevertedForwardedToCareTeamComments,
		recommendation.shouldCareTeamReview,
		callApi,
		infoPopUpAction,
		applicationId,
		srcmId,
		selectedRole,
		navigateWithApplicationId,
	]);

	const onSubmitClick = useCallback(() => {
		/* Show new popup text when unchecking the checkbox and submit */
		const previous = application?.approverRevertedForwardedToCareTeam;
		const current = recommendation.shouldCareTeamReview;
		const approverRevertedForwardedToCareTeam =
			(previous === undefined || previous === false) &&
			current === false &&
			application?.approverForwardedToCareTeam;

		const descriptionKey = approverRevertedForwardedToCareTeam
			? 'youHaveUnselectedForwardToCareTeam'
			: 'onceSubmittedFeedbackCannotBeChanged';

		confirmPopUpAction('confirmSubmit', descriptionKey, onConfirmSubmit);
	}, [
		confirmPopUpAction,
		onConfirmSubmit,
		recommendation.shouldCareTeamReview,
		application?.approverRevertedForwardedToCareTeam,
		application?.approverForwardedToCareTeam,
	]);

	const onCancelClick = useCallback(() => {
		navigateWithApplicationId(INTERVIEW_LANDING);
	}, [navigateWithApplicationId]);

	const handleFormValuesChange = useCallback(async () => {
		try {
			const fieldValues = await form.validateFields({ validateOnly: true });
			setRecommendation(fieldValues);
			if (
				fieldValues.review === ReviewAction.REJECT &&
				!fieldValues.predefinedReasons?.length
			) {
				setSubmitButtonDisabled(true);
			} else {
				setSubmitButtonDisabled(false);
			}
		} catch (e) {
			const fields = e as { values: Feedback; outOfDate: boolean };

			if (!fields.outOfDate) {
				setRecommendation(fields.values);
				setSubmitButtonDisabled(true);
			}
		}
	}, [form]);

	useEffect(() => {
		void handleFormValuesChange();
	}, [handleFormValuesChange, values]);

	const loadApplication = useCallback(async () => {
		try {
			const fetchedApplication: Application = await fetchApplication();
			setRecommendation(fetchedApplication?.approverRecommendation);
			setIsApprover(fetchedApplication?.isApprover);
			setApplication(fetchedApplication);

			form.setFieldsValue({
				...fetchedApplication?.approverRecommendation,
				reapplyCondition: fetchedApplication.reapplyCondition,
			});
		} catch (e) {
			//
		}
	}, [fetchApplication, form]);

	useEffect(() => {
		void loadApplication();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [form]);

	return {
		form,
		application,
		submitButtonDisabled,
		onSubmitClick,
		onCancelClick,
		isApprover,
	};
};
